import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * AdminMirrorUsage — a small live view (admin-only) of how The Mirror is
 * being used and roughly how much it's costing us. Reads from
 * `GET /api/mirror/admin/usage`.
 *
 * Kept intentionally quiet: totals row, a slim sparkline for daily
 * turns, and a subtle spend bar for daily cost. Not a full analytics UI.
 */
export default function AdminMirrorUsage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/mirror/admin/usage", { params: { days: 30 } });
        if (alive) setData(data);
      } catch (e) {
        if (alive) setErr("Couldn't load Mirror usage.");
      }
    })();
    return () => { alive = false; };
  }, []);

  if (err) return null;
  if (!data) {
    return (
      <div className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="admin-mirror-usage-loading">
        Loading Mirror usage…
      </div>
    );
  }

  const t = data.totals || {};
  const series = data.series || [];
  const maxTurns = Math.max(1, ...series.map((d) => d.turns || 0));
  const maxSpend = Math.max(0.01, ...series.map((d) => d.spend || 0));
  const spend = (t.estimated_spend_usd || 0).toFixed(2);

  return (
    <section
      className="p-6 md:p-7 rounded-md border hairline bg-[hsl(var(--paper))]"
      data-testid="admin-mirror-usage"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]">
            Admin · The Mirror
          </div>
          <h3 className="font-display text-2xl mt-2 tracking-tight" data-testid="admin-mirror-usage-heading">
            Usage & cost, last {data.window_days || 30} days
          </h3>
        </div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
          {data.provider} · {data.model}
        </div>
      </div>

      {/* Totals row */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Sessions" value={t.sessions ?? 0} testid="admin-mirror-total-sessions" />
        <Stat label="Turns" value={t.turns ?? 0} testid="admin-mirror-total-turns" />
        <Stat label="Members" value={t.unique_users ?? 0} testid="admin-mirror-total-members" />
        <Stat label="Estimated spend" value={`$${spend}`} testid="admin-mirror-total-spend" />
      </div>

      {/* Daily turns — sparkline bars */}
      <div className="mt-8">
        <div className="flex items-baseline justify-between">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
            Daily turns
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
            peak {maxTurns}
          </div>
        </div>
        <div className="mt-3 flex items-end gap-[3px] h-16 relative" data-testid="admin-mirror-turns-chart">
          {series.map((d) => {
            const h = Math.max(2, Math.round((d.turns / maxTurns) * 64));
            return (
              <div
                key={d.date}
                className="group/bar relative flex-1 rounded-t-sm cursor-default"
                style={{
                  height: `${h}px`,
                  background: "hsl(var(--copper))",
                  opacity: 0.35 + 0.6 * (d.turns / maxTurns),
                }}
                data-testid={`admin-mirror-bar-${d.date}`}
              >
                {/* Rich hover tooltip — date, turns, spend */}
                <div
                  className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover/bar:opacity-100 transition-opacity z-20 whitespace-nowrap rounded-md border hairline bg-[hsl(var(--paper))] px-3 py-2 shadow-md text-left"
                  role="tooltip"
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--copper))]">
                    {d.date}
                  </div>
                  <div className="mt-1 font-display text-sm leading-none">
                    {d.turns} turn{d.turns === 1 ? "" : "s"}
                  </div>
                  <div className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">
                    est. ${(d.spend || 0).toFixed(3)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Daily estimated spend — softer secondary strip */}
      <div className="mt-6">
        <div className="flex items-baseline justify-between">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
            Daily spend (est.)
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
            peak ${maxSpend.toFixed(3)}
          </div>
        </div>
        <div className="mt-2 flex items-end gap-[3px] h-6" data-testid="admin-mirror-spend-chart">
          {series.map((d) => {
            const h = Math.max(1, Math.round(((d.spend || 0) / maxSpend) * 24));
            return (
              <div
                key={`s-${d.date}`}
                className="flex-1 rounded-t-sm"
                style={{
                  height: `${h}px`,
                  background: "hsl(var(--clay))",
                  opacity: 0.5,
                }}
              />
            );
          })}
        </div>
      </div>

      <p className="mt-6 text-[11px] leading-relaxed text-[hsl(var(--muted-foreground))]">
        Spend is estimated from turn volume × average token profile
        ({data.cost_model?.avg_in_tokens_per_turn}
        in / {data.cost_model?.avg_out_tokens_per_turn} out per turn) at
        ${data.cost_model?.in_per_million}/${data.cost_model?.out_per_million} per 1M tokens.
        Real invoice from the Universal Key is authoritative.
      </p>
    </section>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="p-4 rounded-md bg-[hsl(var(--sand))]/40 border hairline" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
        {label}
      </div>
      <div className="mt-1 font-display text-2xl leading-none">{value}</div>
    </div>
  );
}
